'use client';

import React, { useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts';
import { useTranslation } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import MarkdownContent from '../Think/MarkdownContent';
import { ThinkStatus as ThinkStatusType } from '@/type/chat';
import { addChartWatermark } from '@/lib/utils/chart-watermark';

interface MessageChartProps {
  chartConfig: any;
  height?: number;
  width?: string;
  className?: string;
  step?: ThinkStatusType;
}

const MessageChart: React.FC<MessageChartProps> = React.memo(
  ({ chartConfig, height = 400, width = '100%', className, step }) => {
    const { t } = useTranslation();
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstance = useRef<echarts.ECharts | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
      if (!chartRef.current || !chartConfig) return;

      let chart: echarts.ECharts | null = null;

      try {
        // 如果已有实例，先销毁
        if (chartInstance.current) {
          chartInstance.current.dispose();
          chartInstance.current = null;
        }

        // 检查是否为暗色模式
        const isDarkMode = document.documentElement.classList.contains('dark');

        // 初始化图表，根据主题选择
        chart = echarts.init(chartRef.current, isDarkMode ? 'dark' : null);
        chartInstance.current = chart;

        // 合并默认配置和用户配置
        const defaultConfig = {
          grid: {
            ...chartConfig.grid,
            left: '5%',
            right: '5%',
            // bottom: '8%',
            // top: '9%',
            // containLabel: true
          },
          backgroundColor: 'transparent',
          textStyle: {
            color: isDarkMode ? '#e5e7eb' : '#1f2937',
          },
          ...chartConfig,
          xAxis: {
            ...chartConfig.xAxis,
            axisLabel: {
              ...chartConfig.xAxis?.axisLabel,
              formatter: (val: any) => chartAxisformatter(val),
            },
          },
          yAxis: {
            ...chartConfig.yAxis,
            axisLabel: {
              ...chartConfig.yAxis?.axisLabel,
              formatter: (val: any) => chartAxisformatter(val),
            },
          },
          series: chartConfig?.series?.map((item: any) => {
            if (item?.label) {
              return {
                ...item,
                label: {
                  ...item?.label,
                  formatter: (param: any) => chartAxisformatter(param?.value),
                },
              };
            }
            return item;
          }),
        };
        // console.log('MessageChart', chartConfig, defaultConfig);

        // 设置图表配置，使用 notMerge: true 避免合并问题
        chart.setOption(defaultConfig, { notMerge: true, lazyUpdate: true });
        addChartWatermark(chart);
        // 监听窗口大小变化
        const handleResize = () => {
          if (chartInstance.current && !chartInstance.current.isDisposed()) {
            chartInstance.current.resize();
          }
        };
        window.addEventListener('resize', handleResize);

        return () => {
          window.removeEventListener('resize', handleResize);
          if (chartInstance.current && !chartInstance.current.isDisposed()) {
            chartInstance.current.dispose();
            chartInstance.current = null;
          }
        };
      } catch (err) {
        console.error('Failed to render chart:', err);
        setError(t('Chart rendering failed'));
      }
    }, [chartConfig, t]);

    if (error) {
      return (
        <MarkdownContent
          content={
            step?.visualization?.tableData && step?.visualization?.tableData !== ''
              ? step?.visualization?.tableData
              : step?.visualization?.textSummary
          }
        />
      );
    }

    return (
      <div
        ref={chartRef}
        style={{ width, height }}
        className={cn(
          'w-full my-5 border border-gray-200 dark:border-[#2B2B2B] rounded-[12px] bg-bg-main',
          className,
        )}
      />
    );
  },
  (prevProps, nextProps) => {
    // 自定义比较函数，只在 chartConfig 实际改变时重新渲染
    return (
      JSON.stringify(prevProps.chartConfig) === JSON.stringify(nextProps.chartConfig) &&
      prevProps.height === nextProps.height &&
      prevProps.width === nextProps.width &&
      JSON.stringify(prevProps.step) === JSON.stringify(nextProps.step)
    );
  },
);

MessageChart.displayName = 'MessageChart';

export default MessageChart;

export function chartAxisformatter(val: any, prefix: string = '') {
  // 处理非数字值
  if (val === null || val === undefined || isNaN(val)) {
    return val;
  }

  // 辅助函数：移除尾部的零
  const removeTrailingZeros = (numStr: string): string => {
    return parseFloat(numStr).toString();
  };

  // 转换为数字
  const numVal = Number(val);
  const sign = numVal < 0 ? -1 : 1;
  const absVal = Math.abs(numVal);

  const withSign = (formatted: string) =>
    sign < 0 ? `-${prefix}${formatted}` : `${prefix}${formatted}`;

  // 万亿级
  if (absVal >= 1e12) return withSign(removeTrailingZeros((absVal / 1e12).toFixed(2)) + 'T');
  // 十亿级
  if (absVal >= 1e9) return withSign(removeTrailingZeros((absVal / 1e9).toFixed(2)) + 'B');
  // 百万级
  if (absVal >= 1e6) return withSign(removeTrailingZeros((absVal / 1e6).toFixed(2)) + 'M');
  // 千级
  if (absVal >= 1e3) return withSign(removeTrailingZeros((absVal / 1e3).toFixed(2)) + 'K');
  // 小数处理（绝对值小于 1 且大于 0）
  if (absVal < 1 && absVal > 0) {
    if (absVal < 1e-6) return withSign(absVal.toExponential(2));
    if (absVal < 1e-3) return withSign(removeTrailingZeros((absVal * 1e6).toFixed(2)) + 'μ');
    return withSign(removeTrailingZeros((absVal * 1e3).toFixed(2)) + 'm');
  }
  // 普通数值，使用 parseFloat 去除尾部零
  return withSign(removeTrailingZeros(absVal.toFixed(2)));
}
